/**
 * The client list — the practice app's spine. Redrawn on the list shell in
 * Wave 15B (C14 §10.6 items 1–2, ADR-0033; T-6.14).
 *
 * Every action is gated at the **UI** level here by the same verb the service
 * enforces at the **API** level (FiveOX ADR-0029). Both, always: a hidden
 * button is not a permission check, and an enforced API with no hidden button
 * teaches users the product is broken.
 *
 * **Search and the kind chip are the server's, not the shell's.** A list read
 * serves the PAN masked (`AAAPA****A`), so a search for a full PAN can only be
 * answered by the service, which holds it whole. `useListTable` therefore gets
 * no `search` and no `filters` here — it sorts, pages and feeds *Export* over
 * what the service returned — and the toolbar is drawn locally in
 * `ListToolbar`'s manner, driving `clients.list({ search, kind })` exactly as
 * the screen did before the redraw (debounced; a keystroke per request turns a
 * search box into a load test).
 *
 * Two columns changed in Wave 2A and both are worth knowing about:
 *
 * - **Type → Kind.** ADR-0015 replaced the flat list with a shape, so the cell
 *   renders `describeKind` — "Company (private limited)", "Individual" — from
 *   one function every screen shares.
 * - **The PAN column can be empty**, and says so with an em dash rather than a
 *   blank (ADR-0016). A blank cell reads as a row that failed to load.
 *
 * **Active or archived** (C27 item 1, UAT report #1): tabs above the table, as
 * the Branches page draws them, sending `status` to the service — an archived
 * client is not on the default read, and is found here to be restored.
 */

import { Badge, Button, Input, NoMatches, Select, Tabs, buttonClass } from '@fiveox/ui'
import { Plus, Users } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  useListTable,
} from '../../components/page'
import { CLIENT, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { type ClientRecord, clients, describeKind, panDisplay } from '../../lib/api/clients'
import { NewClientDialog } from './NewClientDialog'

/** The most one read may carry (the service's `le=200`). Sort, paging and
 *  export work on what the page holds (ADR-0033, Consequences). */
export const CLIENT_PAGE = 200

export const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'individual', label: 'Individuals' },
  { value: 'business', label: 'Businesses' },
]

/** The list's two statuses, as `GET /api/v1/clients?status=` takes them. */
export type ClientStatusFilter = 'active' | 'archived'

export function clientColumns(): ListColumn<ClientRecord>[] {
  return [
    {
      key: 'legal_name',
      label: 'Client',
      width: 240,
      required: true,
      phone: true,
      sortField: 'legal_name',
      exportValue: (row) => row.legal_name,
      render: (row) => (
        <span className="block min-w-0">
          <span className="block max-w-[10rem] truncate sm:max-w-[15rem]">{row.legal_name}</span>
          {row.display_name && (
            <span className="text-muted-foreground block max-w-[10rem] truncate text-xs font-normal sm:max-w-[15rem]">
              {row.display_name}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'kind',
      label: 'Kind',
      width: 150,
      phone: true,
      sortField: 'kind',
      exportValue: (row) => describeKind(row),
      // Wraps rather than running off a phone: *Company (Private limited)*
      // is the longest, and it is the second of the two columns a phone shows.
      render: (row) => (
        <span className="text-muted-foreground block max-w-[8rem] whitespace-normal sm:max-w-[9rem]">
          {describeKind(row)}
        </span>
      ),
    },
    {
      key: 'pan',
      label: 'PAN',
      width: 110,
      sortField: 'pan',
      // The list read's PAN is masked, and so is the sheet: an export is a
      // copy that leaves the building.
      exportValue: (row) => panDisplay(row.pan),
      render: (row) => (
        <span className="text-muted-foreground font-mono text-xs">{panDisplay(row.pan)}</span>
      ),
    },
    {
      key: 'gstin',
      label: 'GSTIN',
      width: 140,
      sortField: 'gstin',
      exportValue: (row) => row.gstin ?? '',
      render: (row) => (
        <span className="text-muted-foreground font-mono text-xs">{row.gstin ?? '—'}</span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      width: 180,
      sortField: 'email',
      exportValue: (row) => row.email ?? '',
      render: (row) => (
        <span className="text-muted-foreground block max-w-[12rem] truncate">{row.email ?? '—'}</span>
      ),
    },
    {
      key: 'tags',
      label: 'Tags',
      width: 120,
      exportValue: (row) => row.tags.join(', '),
      render: (row) =>
        row.tags.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {row.tags.map((tag) => (
              <Badge key={tag} size="sm">
                {tag}
              </Badge>
            ))}
          </span>
        ),
    },
  ]
}

export function Clients() {
  const { can } = useEvaluation()
  const mayCreate = can(CLIENT, 'can_create')

  const [rows, setRows] = useState<ClientRecord[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState<ClientStatusFilter>('active')
  const [loading, setLoading] = useState(true)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const latest = useRef(0)

  const load = useCallback(async () => {
    const ticket = ++latest.current
    setLoading(true)
    setError(null)
    try {
      const body = await clients.list({
        search: search.trim() || undefined,
        kind: kind || undefined,
        status,
        limit: CLIENT_PAGE,
      })
      // A slow answer to an older search must not overwrite a newer one.
      if (ticket !== latest.current) return
      setRows(body.data)
      setTotal(Number(body.meta?.total ?? body.data.length))
    } catch (err) {
      if (ticket !== latest.current) return
      // The service sends a machine code; render what it means, not "error".
      setError(err instanceof ApiError ? err.message : 'The client list could not be loaded.')
    } finally {
      if (ticket === latest.current) {
        setLoading(false)
        setLoaded(true)
      }
    }
  }, [search, kind, status])

  useEffect(() => {
    // Debounced: a keystroke per request turns a search box into a load test.
    const timer = setTimeout(() => void load(), search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  const columns = useMemo(() => clientColumns(), [])
  const list = useListTable(rows, { columns, initialSort: { field: 'legal_name', dir: 'asc' } })

  const narrowed = search.trim() !== '' || kind !== ''
  const narrowedBy = [
    search.trim(),
    kind ? `Kind: ${KIND_OPTIONS.find((o) => o.value === kind)?.label ?? kind}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
  const reset = () => {
    setSearch('')
    setKind('')
  }

  const addButton = mayCreate ? (
    <Button onClick={() => setCreating(true)}>
      <Plus size={16} aria-hidden /> Add client
    </Button>
  ) : undefined

  const description = !loaded
    ? 'The clients your firm works for.'
    : status === 'archived'
      ? `${total === 1 ? '1 archived client' : `${total} archived clients`}.`
      : `${total === 1 ? '1 client' : `${total} clients`} your firm works for.`

  return (
    <>
      <ListPage
        title="Clients"
        description={description}
        primary={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/people" className={buttonClass({ variant: 'outline' })}>
              <Users size={16} aria-hidden /> People
            </Link>
            {addButton}
          </div>
        }
        // Only the first read is a page-load: a later search keeps the toolbar
        // (and the box being typed in) on screen and shades the rows instead.
        loading={!loaded}
        error={error}
        // The first-run empty state is for a firm with no clients at all; an
        // empty *Archived* tab is said in the table, under the tabs.
        isEmpty={loaded && !narrowed && status === 'active' && rows.length === 0}
        empty={{
          title: 'No clients yet',
          description: mayCreate
            ? 'Add the people and businesses your firm works for. A PAN is optional — an advocate’s client may not have one.'
            : 'When your firm adds a client, they appear here.',
          action: mayCreate ? (
            <Button onClick={() => setCreating(true)}>
              <Plus size={16} aria-hidden /> Add your first client
            </Button>
          ) : undefined,
        }}
        toolbar={
          <div className="space-y-3">
            <Tabs
              idBase="clients-status"
              active={status}
              onChange={(id) => setStatus(id as ClientStatusFilter)}
              tabs={[
                { id: 'active', label: 'Active' },
                { id: 'archived', label: 'Archived' },
              ]}
            />
            <div data-list-toolbar="" className="flex flex-wrap items-center gap-3">
              <div className="w-full sm:w-80">
                <Input
                  size="sm"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  // Name, PAN, GSTIN, email or mobile — and a client with no PAN
                  // is findable by every one of the others (ADR-0016).
                  placeholder="Search by name, PAN, GSTIN, email or mobile"
                  aria-label="Search clients"
                />
              </div>
              <Select
                size="sm"
                className="w-[calc(50vw-2rem)] sm:w-auto"
                aria-label="Kind"
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              >
                <option value="">Everyone</option>
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <span className="text-muted-foreground ml-auto text-xs" data-list-count="">
                {rows.length < total ? `${rows.length} of ${total}` : `${total} ${total === 1 ? 'client' : 'clients'}`}
              </span>
              <Button variant="ghost" size="sm" onClick={reset} disabled={!narrowed}>
                Reset
              </Button>
              <ExportButton rows={list.sorted} columns={columns} filename="prodesk-clients" />
            </div>
          </div>
        }
      >
        {narrowed && !loading && rows.length === 0 ? (
          <NoMatches
            query={narrowedBy}
            action={
              <Button variant="outline" size="sm" onClick={reset}>
                Clear the search and filters
              </Button>
            }
          >
            No client matches. Search by name, PAN, GSTIN, email or mobile, or clear the search to
            see everyone.
          </NoMatches>
        ) : (
          <>
            <ListTable
              list={list}
              loading={loading}
              rowKey={(row) => row.id}
              rowHref={(row) => `/clients/${row.id}`}
            />
            {!loading && status === 'archived' && rows.length === 0 && (
              <p className="text-muted-foreground mt-3 text-sm" data-list-archived-empty="">
                No archived clients. A client you archive from its page is kept here, and can be
                restored.
              </p>
            )}
            {!loading && rows.length < total && (
              <p className="text-muted-foreground mt-3 text-xs" data-list-capped="">
                Showing the first {rows.length} of {total}. Search to find the rest.
              </p>
            )}
          </>
        )}
      </ListPage>

      {creating && (
        <NewClientDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}
    </>
  )
}
